import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectIsMenuOpen, toggleMenuOpen } from "@/store/ui/ui.slice"
import { Link, useLocation, useMatch } from "react-router-dom"
import classNames from "classnames"
import {
  selectMemberDetails,
  selectProductNumbers,
  useMemberDetailsQuery,
} from "@/store/apis/linkApi"
import { selectMemberId } from "@/store/member/member.slice"
import { useIlbaEligibility } from "@/utils/hooks/useIlbaEligibility"

import {
  formatHeaderDate,
  formatName,
  parseMatrixDate,
} from "@/utils/helpers/formatters"
import { useInboxUnread } from "@/utils/hooks/useInbox"

import govCrest from "src/img/govcrest.svg"
import gesbLogo from "img/gesb-logo.svg"
import envelope from "img/002-envelope.svg"
import user from "img/001-user.svg"
import { LogoutButton } from "@components/Utils/Utils"
import { getLastLogin } from "@/utils/helpers/configs"
import GenesysWebChatInitializer from "@/utils/genesysWebChatInitializer/GenesysWebChatInitializer"

function LastLogin() {
  const lastLogin = getLastLogin()
  if (lastLogin) {
    const formatted = formatHeaderDate(parseMatrixDate(lastLogin))
    return (
      <p>
        Welcome back, {<MemberName />}. You last logged in on {formatted}.
      </p>
    )
  }

  return <p>Welcome, {<MemberName />}.</p>
}

function MemberName() {
  const { givenNames, lastName } = useSelector(selectMemberDetails)

  return (
    <>
      <strong>
        {formatName(givenNames)} {formatName(lastName)}
      </strong>
    </>
  )
}

function MemberId() {
  const memberId = useSelector(selectMemberId)
  if (memberId) {
    return (
      <span className="header__member-id">
        <strong>Member number:</strong> {memberId}
      </span>
    )
  }

  return null
}

function UnreadItems() {
  const { unreadTotal } = useInboxUnread()

  return (
    <>
      {unreadTotal ? (
        <span className="unread__items">
          {unreadTotal > 9 ? "9+" : unreadTotal}{" "}
          <span className="sr-only">unread inbox items</span>
        </span>
      ) : (
        ""
      )}
    </>
  )
}

function Header() {
  const dispatch = useDispatch()
  const memberId = useSelector(selectMemberId)
  const { needsHeaderIcon } = useIlbaEligibility(memberId as string)

  const locationObj = useLocation()

  const isMenuOpen = useSelector(selectIsMenuOpen)

  // Move useMatch hook to top to avoid "rendered fewer hooks" error
  const inboxMatch = useMatch("inbox")

  function rootLevelPathMatch(string: string) {
    const { pathname } = locationObj
    const topLevelPath = pathname.split("/").filter((e) => e)?.[0]
    return topLevelPath === string
  }

  // try and keep the header even when rendering as ErrorBoundary
  // since react unmounts everything on error we lose the initial
  // rtk subscription so this is just to ensure its kept subscribed
  const { isSuccess, data } = useMemberDetailsQuery()
  const productIds = useSelector(selectProductNumbers)

  if (isSuccess && !data.member) {
    return null
  }

  return (
    <>
      {isSuccess ? <GenesysWebChatInitializer /> : null}
      <header className="header header--affix">
        <div className="header__container">
          <div className="header__logos">
            <Link to="/" title="Go to Member Online dashboard">
              <span className="sr-only">Go to GESB Member Online homepage</span>
              <img
                height="100"
                src={govCrest}
                className="gov-logo"
                alt="Government of Western Australia logo"
              />
              <img
                height="90"
                src={gesbLogo}
                className="gesb-logo"
                alt="GESB logo"
              />
            </Link>
          </div>
          <div className="header-info">
            <div className="header-user-text">
              {isSuccess ? (
                <>
                  <LastLogin />
                  <MemberId />
                  <GenesysWebChatInitializer />
                </>
              ) : (
                <>
                  {/* Padding so UI doesn't visually jump once these components loaded  */}
                  <p>&nbsp;</p>
                  <span>&nbsp;</span>
                </>
              )}
            </div>
            <div className="header-info__navitems">
              <div className="header-info__hamburger">
                <button
                  className="hamburger-btn"
                  onClick={() => dispatch(toggleMenuOpen())}
                  aria-expanded={isMenuOpen}
                >
                  Menu
                </button>
              </div>
              <div className="header-info__heading">
                <h1>Member Online</h1>
              </div>
              <div className="header-info-icons">
                <div className="header-info-icons__item">
                  <Link
                    to="/inbox"
                    className={classNames(
                      "header-icon-btn",
                      inboxMatch ? "header-icon-btn--active" : ""
                    )}
                  >
                    {!!productIds.length && <UnreadItems />}
                    <img src={envelope} alt="Envelope icon" />
                    <span className="header-icon-btn__text">
                      Inbox and statements
                    </span>
                    {/* <span className="sr-only">You have 2 new messages</span> */}
                  </Link>
                </div>
                <div className="header-info-icons__item">
                  <Link
                    to="/personal-details"
                    className={classNames(
                      "header-icon-btn",
                      rootLevelPathMatch("personal-details")
                        ? "header-icon-btn--active"
                        : ""
                    )}
                  >
                    {needsHeaderIcon && (
                      <span className="unread__items">
                        ! <span className="sr-only">action required</span>
                      </span>
                    )}
                    <img src={user} alt="User icon" />
                    <span className="header-icon-btn__text">
                      Personal details
                    </span>
                  </Link>
                </div>
                <div className="header-info-icons__item hide-mobi">
                  <LogoutButton />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="header-user-text--mobi header-user-text">
          {isSuccess && (
            <>
              <LastLogin />
              <div>
                <MemberId />
                <LogoutButton />
              </div>
            </>
          )}
        </div>
      </header>
    </>
  )
}

export default Header
